import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  CSSProperties
} from 'react'
import { Checkbox, Empty } from 'antd'
import dayjs from 'dayjs'
import CONFIG from '../../constants/config'
import sensors from '../../utils/sensors'
import * as MapUtils from '../../utils/mapUtils'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
  setCurrentUser,
  mapCheckUserDetail,
  viewPhoto
} from '../../actions/pathway'
import timeline_icon_start from '../../images/pathway/timeline_icon_start.png'
import timeline_icon_end from '../../images/pathway/timeline_icon_end.png'
import timeline_icon_mid from '../../images/pathway/timeline_icon_mid.png'
import map_detail_more_photo from '../../images/pathway/map_detail_more_photo.png'
import timeline_icon_sj from '../../images/pathway/timeline_icon_sj.png'
import { LeftOutlined } from '@ant-design/icons'
import type { Pathway } from '../../slices/pathwaySlice'

interface UserDetailProps {
  viewer: any
}

function UserDetail({ viewer }: UserDetailProps) {
  const dispatch = useAppDispatch()
  const uncheckedPointIndexRef = useRef<number[]>([])

  // local state
  const [locationItemHeight, setLocationItemHeight] = useState(0)

  // Redux state
  const currentUser = useAppSelector((state) => state.pathway.currentUser)
  const pathways = useAppSelector((state) => state.pathway.pathways)
  const groupId = useAppSelector((state) => state.group.groupId)
  const checkedMembers = useAppSelector((state) => state.pathway.checkedMembers)

  // componentDidMount - 获取 location item 高度
  useEffect(() => {
    const locationItem = document.getElementsByClassName('location')[0] as
      | HTMLElement
      | undefined
    if (locationItem) {
      setLocationItemHeight(locationItem.offsetHeight)
    }
  }, [])

  // 返回列表
  const handleBackToList = useCallback(() => {
    dispatch(setCurrentUser(null))
    const pathwaysInMap = pathways.filter((pathway) =>
      checkedMembers.includes(pathway.userID)
    )
    MapUtils.drawPathsV2({
      pathways: pathwaysInMap,
      checkedMembers: checkedMembers,
      createInfoWindow: (pathIndexInMap, markerIndex) =>
        MapUtils.createLocationInfoWindowV2({
          pathway: pathwaysInMap[pathIndexInMap],
          markerIndex,
          onButtonClick: () => {
            dispatch(mapCheckUserDetail(pathwaysInMap[pathIndexInMap], viewer))
            sensors.track('web_pathway_homepage_click', {
              clickItem: 'routeNodeDetail',
              groupID: groupId,
              locationName:
                pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]?.texts
                  ?.addr,
              photoNum:
                pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]?.photos
                  ?.length || 0
            })
          },
          onViewPhoto: (photoIndex) => {
            dispatch(
              viewPhoto(
                pathwaysInMap[pathIndexInMap],
                markerIndex,
                photoIndex,
                viewer
              )
            )
            sensors.track('web_pathway_homepage_click', {
              clickItem: 'routeNodePic',
              groupID: groupId,
              locationName:
                pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]?.texts
                  ?.addr,
              photoNum:
                pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]?.photos
                  ?.length || 0
            })
          },
          canCheckDetail: true
        })
    })
  }, [dispatch, pathways, checkedMembers, groupId, viewer])

  // 勾选/取消勾选位置点
  const handleCheckLocationPoint = useCallback(
    (check: boolean, index: number) => {
      if (!currentUser) return
      const currentUserPathway = pathways.filter(
        (pathway: Pathway) => pathway.userID === currentUser.userID
      )

      if (check) {
        uncheckedPointIndexRef.current = uncheckedPointIndexRef.current.filter(
          (item) => item !== index
        )
        sensors.track('web_pathway_homepage_click', {
          clickItem: 'checkRouteNode',
          groupID: groupId,
          locationName: currentUserPathway[0]?.locations?.[index]?.texts?.addr,
          photoNum:
            currentUserPathway[0]?.locations?.[index]?.photos?.length || 0
        })
      } else {
        uncheckedPointIndexRef.current =
          uncheckedPointIndexRef.current.concat(index)
        sensors.track('web_pathway_homepage_click', {
          clickItem: 'uncheckRouteNode',
          groupID: groupId,
          locationName: currentUserPathway[0]?.locations?.[index]?.texts?.addr,
          photoNum:
            currentUserPathway[0]?.locations?.[index]?.photos?.length || 0
        })
      }

      let newPathways = currentUserPathway.map((pathway: Pathway) => {
        if (currentUser && pathway.userID === currentUser.userID) {
          return {
            ...pathway,
            locations: pathway.locations.filter(
              (location, idx) => !uncheckedPointIndexRef.current.includes(idx)
            )
          }
        }
        return pathway
      })

      MapUtils.drawPathsV2({
        pathways: newPathways,
        // 如果查看的人，是已选择列表中的某人，则使用已分配颜色；如果是未勾选的某人，则使用第一个颜色
        checkedMembers:
          currentUser &&
          checkedMembers.some((userID) => userID === currentUser.userID)
            ? checkedMembers
            : currentUser
              ? [currentUser.userID]
              : [],
        createInfoWindow: (pathIndexInMap, markerIndex) =>
          MapUtils.createLocationInfoWindowV2({
            pathway: newPathways[pathIndexInMap],
            markerIndex,
            onButtonClick: () => {
              dispatch(mapCheckUserDetail(newPathways[pathIndexInMap], viewer))
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'routeNodeDetail',
                groupID: groupId,
                locationName:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.texts
                    ?.addr,
                photoNum:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.photos
                    ?.length || 0
              })
            },
            onViewPhoto: (photoIndex) => {
              dispatch(
                viewPhoto(
                  newPathways[pathIndexInMap],
                  markerIndex,
                  photoIndex,
                  viewer
                )
              )
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'routeNodePic',
                groupID: groupId,
                locationName:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.texts
                    ?.addr,
                photoNum:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.photos
                    ?.length || 0
              })
            },
            canCheckDetail: false
          }),
        resetMapView: false
      })
    },
    [dispatch, pathways, currentUser, checkedMembers, groupId, viewer]
  )

  // 渲染路线图标
  const renderPathwayIcon = useCallback(
    (index, locationNum) => {
      const moreThanOne = locationNum > 1
      const canCheckDetail = locationNum > 2
      if (index === 0 && moreThanOne) {
        if (canCheckDetail) {
          return (
            <>
              <img
                className="start-end"
                style={{ top: '20%' }}
                src={timeline_icon_start}
                alt=""
              />
              <Checkbox
                className="checkbox"
                style={{ top: '90%' }}
                defaultChecked
                onChange={(e) =>
                  handleCheckLocationPoint(e.target.checked, index)
                }
              />
            </>
          )
        }
        return <img className="start-end" src={timeline_icon_start} alt="" />
      } else if (index === locationNum - 1 && moreThanOne) {
        if (canCheckDetail) {
          return (
            <>
              <img
                className="start-end"
                style={{ top: '90%' }}
                src={timeline_icon_end}
                alt=""
              />
              <Checkbox
                className="checkbox"
                style={{ top: '0%' }}
                defaultChecked
                onChange={(e) =>
                  handleCheckLocationPoint(e.target.checked, index)
                }
              />
            </>
          )
        }
        return <img className="start-end" src={timeline_icon_end} alt="" />
      } else if (moreThanOne) {
        return (
          <Checkbox
            className="checkbox"
            defaultChecked
            onChange={(e) => handleCheckLocationPoint(e.target.checked, index)}
          />
        )
      }
    },
    [handleCheckLocationPoint]
  )

  // 渲染路线详情
  const renderPathwayDetail = useCallback(() => {
    if (!currentUser) return null
    const pathway = pathways.find(
      (pathway: Pathway) => pathway.userID === currentUser.userID
    )
    if (pathway && pathway.locations?.length) {
      const locationNum = pathway.locations.length
      const canCheckDetail = locationNum > 2
      const dashHeight =
        locationNum > 1
          ? canCheckDetail
            ? 28 + (locationNum - 1) * (locationItemHeight + 20) + 50 + 40
            : 28 + (locationNum - 1) * (locationItemHeight + 20) + 50
          : 0
      return (
        <div className="pathway-detail">
          <div className="dash" style={{ height: `${dashHeight}px` }}></div>
          <div className="user-info">
            <img src={currentUser.headimgurl} alt="" />
            <span>{currentUser.nickname}</span>
          </div>
          {pathway.locations.map((location, index) => {
            const endTime =
              location.photos?.length > 1
                ? dayjs
                    .unix(
                      location.photos[location.photos.length - 1]?.time || 0
                    )
                    .format(CONFIG.hourMinuteFormat)
                : ''
            const photos = location.photos?.slice(0, 3) || []
            const photosNum = pathway.locations[index]?.photos?.length || 0
            return (
              <div
                className="location"
                style={
                  index === 0 && canCheckDetail
                    ? { marginTop: '40px' }
                    : undefined
                }
                key={index}
              >
                <div className="time-address">
                  <p className="time">
                    {dayjs
                      .unix(location.photos?.[0]?.time || 0)
                      .format(CONFIG.hourMinuteFormat)}
                    {endTime ? `~${endTime}` : ''}
                  </p>
                  <p className="address">{location.texts?.addr}</p>
                  {renderPathwayIcon(index, locationNum)}
                </div>
                <div className="photos">
                  {photos.map((photo, photoIndex) => (
                    <div
                      className="photo"
                      key={photoIndex}
                      style={{
                        backgroundImage: `url(${photo.smallurl})`
                      }}
                      onClick={() => {
                        dispatch(viewPhoto(pathway, index, photoIndex, viewer))
                        sensors.track('web_pathway_memberdetail_click', {
                          clickItem: 'routeNodePic',
                          groupID: groupId,
                          locationName: location.texts?.addr,
                          photoNum: location.photos?.length || 0
                        })
                      }}
                    >
                      {photosNum > 3 && photoIndex === 2 && (
                        <div className="more">
                          <img src={map_detail_more_photo} alt="" />
                          <span>{photosNum}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {index < pathway.locations.length - 1 && (
                  <div className="time-consumption">
                    <p className="real">{location.texts?.consumptionOfTime}</p>
                    <p className="walk">{location.texts?.predictWalk}</p>
                    <p className="drive">{location.texts?.predictDrive}</p>
                    <img className="triangle" src={timeline_icon_sj} alt="" />
                    <img
                      className="consumption-point"
                      src={timeline_icon_mid}
                      alt=""
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )
    } else {
      return (
        <div className="pathway-detail">
          <div className="user-info">
            <img src={currentUser.headimgurl} alt="" />
            <span>{currentUser.nickname}</span>
          </div>
          <Empty
            style={{ marginTop: '210px' }}
            description={
              <>
                <span>该成员当日未拍照</span>
                <br />
                <span>可查询其他日期</span>
              </>
            }
          />
        </div>
      )
    }
  }, [
    dispatch,
    pathways,
    currentUser,
    locationItemHeight,
    groupId,
    viewer,
    renderPathwayIcon
  ])

  return (
    <div className="user-detail">
      <div className="back-btn" onClick={handleBackToList}>
        <LeftOutlined />
        返回员工列表
      </div>
      {renderPathwayDetail()}
    </div>
  )
}

export default UserDetail
